import {
  AFFILIATION_ELEMENTS,
  AFFILIATION_ELEMENT_PASSIVE_LINES,
  AFFILIATION_ELEMENT_TITLES,
} from "./affiliationInfo"
import {
  DAILY_QUIZ_REWARD,
  DEFAULT_MAX_ENERGY,
  ENERGY_REGEN_PER_HOUR,
  MAX_LEVEL,
  ONE_ON_ONE_LOSE_PTS,
  ONE_ON_ONE_WIN_COINS,
  ONE_ON_ONE_WIN_PTS,
  QR_BATTLES_DAILY_LIMIT,
  RADAR_ENERGY_COST,
  RADAR_LOSE_PTS,
  RADAR_WIN_COINS,
  RADAR_WIN_PTS,
  RANK_BASE,
  RANK_EXAM_PASS_SCORE,
  RANK_EXAM_TOTAL,
  RANK_ORDER,
  TRAINING_COINS_PER_WIN,
  TRAINING_DAILY_REWARD_LIMIT,
  fuseSuccessPercent,
} from "./game.constants"
import { DAILY_QUEST_LABELS, WEEKLY_QUEST_LABELS } from "./gameMeta"
import { ColorRank } from "../types/battle"

export type RulesTable = {
  headers: string[]
  rows: string[][]
}

export type GameRulesBlock =
  | { type: "paragraphs"; paragraphs: string[] }
  | { type: "bullets"; bullets: string[] }
  | { type: "table"; table: RulesTable; caption?: string }
  | { type: "highlight"; text: string }
  | { type: "note"; text: string }

export type GameRulesScope = "virtual" | "physical"

export type GameRulesSection = {
  number: number
  title: string
  intro?: string
  blocks: GameRulesBlock[]
  /** По умолчанию — правила приложения (виртуальная игра) */
  scope?: GameRulesScope
}

const RANK_LABEL: Record<ColorRank, string> = {
  WHITE: "Белый",
  GREEN: "Зелёный",
  BLUE: "Синий",
  PURPLE: "Фиолетовый",
  ORANGE: "Оранжевый",
  RED: "Красный",
}

/** Пороги экзамена (backend getExamLevelGate) — ORANGE/RED только через слияние */
const EXAM_LEVEL: Record<ColorRank, number | null> = {
  WHITE: 10,
  GREEN: 20,
  BLUE: 30,
  PURPLE: 40,
  ORANGE: null,
  RED: null,
}

function rankRulesTable(): RulesTable {
  return {
    headers: ["Ранг", "Макс. уровень", "База силы"],
    rows: RANK_ORDER.map((rank) => [
      RANK_LABEL[rank],
      String(MAX_LEVEL[rank]),
      RANK_BASE[rank].toLocaleString("ru-RU"),
    ]),
  }
}

function examThresholdTable(): RulesTable {
  return {
    headers: ["Текущий ранг", "Уровень для экзамена"],
    rows: RANK_ORDER.filter((r) => EXAM_LEVEL[r] != null).map((rank) => [
      RANK_LABEL[rank],
      String(EXAM_LEVEL[rank]),
    ]),
  }
}

function fuseChanceTable(): RulesTable {
  return {
    headers: ["Ранг", "Шанс слияния"],
    rows: RANK_ORDER.map((rank) => [
      RANK_LABEL[rank],
      rank === "RED" ? "—" : `${fuseSuccessPercent(rank)}%`,
    ]),
  }
}

function dailyQuestsTable(): RulesTable {
  const keys = Object.keys(DAILY_QUEST_LABELS) as (keyof typeof DAILY_QUEST_LABELS)[]
  return {
    headers: ["Квест", "Задача", "Монеты", "Чакра"],
    rows: keys.map((key) => {
      const q = DAILY_QUEST_LABELS[key]
      return [
        q.title,
        q.description,
        q.rewardCoins > 0 ? String(q.rewardCoins) : "—",
        q.rewardChakra > 0 ? String(q.rewardChakra) : "—",
      ]
    }),
  }
}

function weeklyQuestsTable(): RulesTable {
  const keys = Object.keys(WEEKLY_QUEST_LABELS) as (keyof typeof WEEKLY_QUEST_LABELS)[]
  return {
    headers: ["Квест", "Задача", "Цель", "Монеты", "Чакра"],
    rows: keys.map((key) => {
      const q = WEEKLY_QUEST_LABELS[key]
      return [
        q.title,
        q.description,
        String(q.target),
        String(q.rewardCoins),
        String(q.rewardChakra),
      ]
    }),
  }
}

function guardianPassivesTable(): RulesTable {
  return {
    headers: ["Стихия", "Пассив (белый–фиолетовый ранг)"],
    rows: AFFILIATION_ELEMENTS.map((element) => [
      AFFILIATION_ELEMENT_TITLES[element],
      AFFILIATION_ELEMENT_PASSIVE_LINES[element],
    ]),
  }
}

export const GUARDIANS_GAME_RULES_INTRO = {
  title: "Правила виртуальной игры",
  subtitle:
    "Приложение «Хранители»: карты, дуэли, академия, город и ресурсы.",
}

export const GUARDIANS_PHYSICAL_RULES_INTRO = {
  title: "Хранители — правила настольной игры",
  subtitle:
    "Цель партии: первым собрать 6 трофеев. Белые — топливо боёв; резерв — план прокачки.",
}

export const GUARDIANS_COMIC_URL = "https://guardians.daks.by"

export const GUARDIANS_GAME_RULES_SECTIONS: GameRulesSection[] = [
  {
    number: 1,
    title: "О чём игра",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Цифровой паспорт — профиль, рейтинг, энергия, монеты",
          "Колода карт — стихии, стили, уровни и цвет рамки",
          "Академия — тесты и экзамены",
          "Точки на карте — сканы с наградами",
          "Дуэли — бой 1 на 1: каждый выбирает одну карту, сервер считает силу",
        ],
      },
      {
        type: "highlight",
        text: "Цель: качать карты, побеждать в дуэлях, поднимать рейтинг и эволюционировать карты до высших рангов.",
      },
    ],
  },
  {
    number: 2,
    title: "С чего начать",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Выберите лигу в Академии: Титан (базовый) или Атлант (продвинутый)",
          "Выберите стихию хранителя — она задаёт пассив в PvP (один раз, до рестарта)",
          "Получите 2 стартовые карты (белый ранг)",
          "Стартовые ресурсы: 100 монет и 100 чакр",
          "Выберите активную карту в паспорте — для профиля (в бою — любая своя)",
          "Пройдите ежедневный тест (3 вопроса)",
          "Отсканируйте точку в городе",
          "Сыграйте первую дуэль — RADAR или 1 на 1",
        ],
      },
    ],
  },
  {
    number: 3,
    title: "Карта: из чего состоит",
    blocks: [
      {
        type: "table",
        table: {
          headers: ["Параметр", "Что это"],
          rows: [
            ["Стихия", "Огонь, Вода, Земля, Молния, Ветер, Лёд"],
            ["Стиль", "⚔️ Атака · 🛡️ Защита · ⚡ Скорость"],
            [
              "Цвет рамки",
              "Белый → Зелёный → Синий → Фиолетовый → Оранжевый → Красный",
            ],
            ["Уровень", "Качается за чакру"],
            ["Базовая сила", "Растёт от слияний и бонусов"],
            ["Перерождение", "Красная карта: +50% к силе в бою"],
          ],
        },
      },
      { type: "note", text: "У каждого ранга свой максимальный уровень:" },
      { type: "table", table: rankRulesTable() },
      {
        type: "paragraphs",
        paragraphs: [
          "Базовая сила = база ранга + (уровень − 1) × 50 + доп. сила с карты",
          "Пример: белая 1 уровня → 100. Белая, 10 уровень → 550.",
        ],
      },
    ],
  },
  {
    number: 4,
    title: "Как проходит дуэль",
    intro: "Не классическая игра с полем и колодой.",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Попадаете в бой (RADAR или комната 1 на 1)",
          "До 60 секунд — тайно выбрать одну карту",
          "Соперник делает то же — его карту не видно до конца",
          "Карты раскрываются, сервер считает боевую силу (БС)",
          "У кого БС больше — победа. Ничья — без очков и монет",
          "Начисляются очки рейтинга; при победе — монеты",
        ],
      },
      {
        type: "note",
        text: "После выбора карту нельзя сменить. Карта соперника скрыта до результата.",
      },
    ],
  },
  {
    number: 5,
    title: "Стихии: кто кого бьёт",
    intro: "Сильная стихия против слабой: бонус % от БС карты. На белом ранге контр-пик сильнее всего.",
    blocks: [
      {
        type: "table",
        table: {
          headers: ["Ранг", "Стихия", "Стиль"],
          rows: [
            ["Белый", "+25%", "+20%"],
            ["Зелёный", "+20%", "+16%"],
            ["Синий", "+15%", "+12%"],
            ["Фиолетовый", "+12%", "+10%"],
            ["Оранжевый", "+10%", "+8%"],
            ["Красный", "+6%", "+4%"],
          ],
        },
      },
      {
        type: "table",
        table: {
          headers: ["Ваша стихия", "Сильна против"],
          rows: [
            ["🔥 Огонь", "Лёд, Молния"],
            ["💨 Ветер", "Земля, Огонь"],
            ["🌍 Земля", "Вода, Лёд"],
            ["💧 Вода", "Огонь, Молния"],
            ["❄️ Лёд", "Ветер, Вода"],
            ["⚡ Молния", "Земля, Ветер"],
          ],
        },
      },
      { type: "note", text: "Если стихии «равны» по кругу — бонуса стихии нет." },
    ],
  },
  {
    number: 6,
    title: "Стили: камень — ножницы — бумага",
    intro: "Отдельно от стихий: бонус стиля, если ваш стиль побеждает стиль соперника. % зависит от ранга карты (см. таблицу выше).",
    blocks: [
      {
        type: "table",
        table: {
          headers: ["Ваш стиль", "Побеждает"],
          rows: [
            ["⚔️ Атака", "🛡️ Защита"],
            ["🛡️ Защита", "⚡ Скорость"],
            ["⚡ Скорость", "⚔️ Атака"],
          ],
        },
      },
      {
        type: "note",
        text: "Стихия и стиль складываются: можно получить оба бонуса, каждый считается от baseBS карты.",
      },
    ],
  },
  {
    number: 7,
    title: "Итоговая сила в бою",
    blocks: [
      {
        type: "highlight",
        text: "БС = базовая сила × школа × reborn + бонус стихии (% от БС) + бонус стиля (% от БС) + пассив хранителя (только PvP)",
      },
      {
        type: "paragraphs",
        paragraphs: [
          "Перерождение: красная карта с флагом перерождения получает ×1.5 к базовой силе в бою.",
          "Пассив хранителя не входит в силу карты в колоде — только в результате PvP-боя.",
        ],
      },
      {
        type: "note",
        text: "Лёд (Морозное касание): режет бонус стихии соперника на 50% и даёт −3% к силе врага. Ветер (Шквал): +5% (+10% на SPEED), спасает только поражение → ничья.",
      },
    ],
  },
  {
    number: 8,
    title: "Два режима боя",
    blocks: [
      {
        type: "bullets",
        bullets: [
          `RADAR — поиск соперника. ${RADAR_ENERGY_COST} энергии за попытку. Подбор ±~150 очков. Победа: +${RADAR_WIN_PTS} очков, +${RADAR_WIN_COINS} монет. Поражение: −${RADAR_LOSE_PTS}. Если соперник вышел до конца — энергия возвращается.`,
          `1 на 1 — комната по коду, энергию не тратит. Победа: +${ONE_ON_ONE_WIN_PTS} очков, +${ONE_ON_ONE_WIN_COINS} монет. Поражение: −${ONE_ON_ONE_LOSE_PTS}.`,
          `Полные награды за победу в 1 на 1 — до ${QR_BATTLES_DAILY_LIMIT} раз в сутки. После лимита бой возможен, но награды за победу могут не начисляться.`,
          "Вызов игрока онлайн — «Свободный бой» или «По рангу». В свободном бою каждый выбирает любую карту из колоды. В бою по рангу — только карты выбранного ранга.",
        ],
      },
      {
        type: "note",
        text: "Свободный бой: без ограничения по рангу — подойдёт любая ваша карта.",
      },
    ],
  },
  {
    number: 9,
    title: "Энергия",
    blocks: [
      {
        type: "table",
        table: {
          headers: ["Параметр", "Значение"],
          rows: [
            ["Стартовый максимум", String(DEFAULT_MAX_ENERGY)],
            ["Тратится", `${RADAR_ENERGY_COST} за бой в RADAR`],
            ["Восстановление", `+${ENERGY_REGEN_PER_HOUR} каждый час`],
          ],
        },
      },
      { type: "note", text: "Без энергии в RADAR не попасть. Бои 1 на 1 энергию не тратят." },
    ],
  },
  {
    number: 10,
    title: "Чакра — прокачка уровня",
    blocks: [
      {
        type: "paragraphs",
        paragraphs: [
          "Чакра на паспорте тратится на +1 уровень выбранной карты.",
          "Стоимость растёт с уровнем (от 50, дальше ~×1.15).",
          "На максимальном уровне ранга — экзамен или слияние.",
        ],
      },
      {
        type: "table",
        table: {
          headers: ["Источник", "Награда"],
          rows: [
            ["Ежедневный тест (3/3)", `+${DAILY_QUIZ_REWARD} чакры`],
            ["Скан точки", "обычно 20–80"],
            ["Квест «Победа в дуэли»", "+25 чакры"],
            ["Квест «Скан»", "+40 чакры"],
            ["Магазин за монеты", "по товару"],
          ],
        },
      },
      { type: "note", text: "Тест раз в 24 часа." },
    ],
  },
  {
    number: 11,
    title: "Монеты",
    blocks: [
      {
        type: "table",
        table: {
          headers: ["Действие", "Монеты"],
          rows: [
            ["Победа в RADAR", `+${RADAR_WIN_COINS}`],
            ["Победа в 1 на 1 (в лимите)", `+${ONE_ON_ONE_WIN_COINS}`],
            [`Тренировка (до ${TRAINING_DAILY_REWARD_LIMIT}/день)`, `+${TRAINING_COINS_PER_WIN}`],
            ["Скан точки", "обычно 50–150"],
            ["Ежедневные квесты", "20–35"],
            ["Еженедельные квесты", "80–120"],
            ["Магазин", "по товару"],
          ],
        },
      },
      {
        type: "note",
        text: "В магазине: энергия, чакра, токен смены стиля (150 монет), усилитель слияния (200 монет) и другие товары.",
      },
    ],
  },
  {
    number: 12,
    title: "Очки рейтинга",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Глобальные и локальные очки — после дуэлей",
          "Таблица лидеров и подбор в RADAR",
          "Ничья — очки не меняются (в т.ч. уклонение ветра)",
          "Пассив воды: при поражении теряете на 25% меньше очков",
        ],
      },
    ],
  },
  {
    number: 13,
    title: "Как улучшить цвет рамки",
    blocks: [
      {
        type: "paragraphs",
        paragraphs: ["Два пути: экзамен или слияние (можно комбинировать)."],
      },
      {
        type: "bullets",
        bullets: [
          `Экзамен: ${RANK_EXAM_TOTAL} вопросов, минимум ${RANK_EXAM_PASS_SCORE} правильных. Успех — ранг +1, уровень 1. Провал — блок 24 ч.`,
          "Слияние: две карты — одна стихия и цвет. Жертва сгорает. Успех — следующий цвет, +40% к базовой силе основной.",
          "Усилитель слияния: +10% к шансу (один расходуется за попытку). Покупается в магазине.",
        ],
      },
      { type: "table", table: examThresholdTable(), caption: "Порог для экзамена" },
      { type: "table", table: fuseChanceTable(), caption: "Базовый шанс слияния" },
      {
        type: "paragraphs",
        paragraphs: [
          "При неудаче слияния жертва удаляется, основная не меняет цвет.",
          `Оранжевая 50 уровня при успешном слиянии (${fuseSuccessPercent("ORANGE")}%) может стать красной с перерождением.`,
          "Смена стиля карты: токен из магазина, один раз на карту (атака / защита / скорость).",
        ],
      },
    ],
  },
  {
    number: 14,
    title: "Сканы точек на карте",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Подойдите к точке (~50 м)",
          "Одна точка — раз в 24 часа",
          "Лут: монеты, чакра, энергия или карта",
        ],
      },
    ],
  },
  {
    number: 15,
    title: "Ежедневные квесты",
    blocks: [{ type: "table", table: dailyQuestsTable() }],
  },
  {
    number: 16,
    title: "Чеклист на каждый день",
    blocks: [
      {
        type: "bullets",
        bullets: [
          `Ежедневный тест (+${DAILY_QUIZ_REWARD} чакры при 3/3)`,
          "Забрать ежедневные и еженедельные квесты",
          "Сканировать точки",
          "Прокачать главную карту",
          "RADAR, 1 на 1 или тренировка",
          "Готовиться к экзамену или слиянию",
        ],
      },
    ],
  },
  {
    number: 17,
    title: "Частые вопросы",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Активная карта для боя не обязательна — в дуэли любая ваша.",
          "Стихия хранителя ≠ стихия карты: пассив огня срабатывает только с картой огня.",
          "Проигрыш при «сильной» карте: бонусы стихии/стиля (% от БС), выше ранг или уровень у соперника.",
          "Дубликаты одной стихии — для слияния или запас.",
          "Монеты: победы, сканы, квесты, тренировка.",
        ],
      },
    ],
  },
  {
    number: 18,
    title: "Стихия хранителя и пассивы PvP",
    intro:
      "Выбирается один раз при онбординге (или после рестарта). Работает только в PvP: радар, 1 на 1, вызовы.",
    blocks: [
      { type: "table", table: guardianPassivesTable() },
      {
        type: "note",
        text: "На оранжевых и красных картах пассив слабее. Вода: −25% потери рейтинга только при поражении, не при ничьей.",
      },
      {
        type: "bullets",
        bullets: [
          "Молния: постоянный % + отдельный крит % при срабатывании",
          "Ветер (Шквал): +5% силы (+10% на SPEED); уклонение только если проиграл по силе — бой становится ничьей",
          "Лёд (Морозное касание): +7% силы, враг −3%; контр-пик стихии врага слабее на 50%",
        ],
      },
    ],
  },
  {
    number: 19,
    title: "Расходники: бустер и токен стиля",
    blocks: [
      {
        type: "table",
        table: {
          headers: ["Предмет", "Где взять", "Эффект"],
          rows: [
            [
              "Усилитель слияния",
              "Магазин (~200 монет)",
              "+10% к шансу слияния за одну попытку",
            ],
            [
              "Токен смены стиля",
              "Магазин (~150 монет)",
              "Сменить стиль карты один раз (атака / защита / скорость)",
            ],
          ],
        },
      },
      {
        type: "note",
        text: "Количество расходников видно в профиле рядом с монетами и чакрой. После покупки или использования паспорт обновляется.",
      },
    ],
  },
  {
    number: 20,
    title: "Тренировочный зал",
    blocks: [
      {
        type: "bullets",
        bullets: [
          `Победа: +${TRAINING_COINS_PER_WIN} монет (без очков рейтинга)`,
          `До ${TRAINING_DAILY_REWARD_LIMIT} боёв в сутки с наградой — дальше можно тренироваться без монет`,
          "Пассивы хранителя и PvP-статистика в тренировке не учитываются",
          "Подходит для отработки карт и ежедневного квеста «Выиграй 2 тренировки»",
        ],
      },
    ],
  },
  {
    number: 21,
    title: "Еженедельные квесты",
    intro: "Вкладка «Неделя» в заданиях. Сброс по понедельнику (Europe/Minsk).",
    blocks: [
      { type: "table", table: weeklyQuestsTable() },
      {
        type: "note",
        text: "Прогресс (в т.ч. «3 разные стихии») считает сервер. Награду забираете вручную после выполнения.",
      },
    ],
  },
  {
    number: 22,
    title: "История PvP-боёв",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "В профиле — «История боёв»: последние PvP-матчи (радар, 1 на 1, вызов)",
          "По каждому бою: соперник, исход, очки и монеты, ваши карты",
          "По тапу — разбор: baseBS, бонусы стихии/стиля, пассив, итоговая сила",
          "Тренировки и боссы в историю не попадают",
        ],
      },
    ],
  },
  {
    number: 23,
    title: "Гильдии",
    blocks: [
      {
        type: "paragraphs",
        paragraphs: [
          "Гильдия — постоянный отряд до 20 игроков одного города. Лиги внутри гильдии можно смешивать. Отдельного чата нет: пишете в чате города и включаете режим «Для гильдии» — остальным текст зашифрован, модераторам виден всегда.",
        ],
      },
      {
        type: "bullets",
        bullets: [
          "Создание: ≥3000 globalPts и 10 000 монет (сразу в банк гильдии)",
          "Название и тег (до 3 символов) уникальны; смена — раз в 6 месяцев за монеты банка",
          "Набор: по заявке (решает только лидер) или по инвайту; отказ → чёрный список на 7 дней",
          "После выхода/кика — кулдаун 24 часа; при роспуске банк сгорает",
          "Квесты недели, сундук, банк, бусты, гербы по уровню, босс недели, GvG",
          "На арене участники одной гильдии/пати стараются попасть в одну команду",
        ],
      },
    ],
  },
  {
    number: 24,
    title: "Банк",
    intro:
      "Геоточка города. Два независимых режима: сейф (монеты и карты) и срочные вклады с процентами.",
    blocks: [
      {
        type: "paragraphs",
        paragraphs: [
          "Все операции с сейфом и вкладами доступны только рядом с точкой банка. Кошелёк (монеты в профиле) — для трат. Сейф — хранение. Вклад — отдельный замороженный пул из кошелька.",
        ],
      },
      {
        type: "bullets",
        bullets: [
          "Сейф монет: мгновенно положить/забрать у банка (coins ↔ bankCoins)",
          "Сейф карт: до 50 слотов, два расширения по +25 за 5000 монет (макс. 100)",
          "Карты в сейфе не занимают рюкзак и недоступны в бою, почте, рынке, слиянии и рейдах",
          "Комиссия хранения сейфа: 10 монет/мес, если есть монеты или карты в сейфе",
          "Списание комиссии: сначала кошелёк, затем сейф; недобор → долг",
          "При долге нельзя класть в сейф, расширять банк, открывать вклад и закрывать вклад досрочно; забрать вклад по сроку можно",
        ],
      },
      {
        type: "table",
        caption: "Срочные вклады (из кошелька, не из сейфа)",
        table: {
          headers: ["Срок", "Доход", "Досрочно"],
          rows: [
            ["3 дня", "2%", "половина дохода"],
            ["7 дней", "5%", "половина дохода"],
            ["30 дней", "12%", "половина дохода"],
          ],
        },
      },
      {
        type: "bullets",
        bullets: [
          "Сумма одного вклада: 100–5000 монет",
          "До 3 активных вкладов одновременно",
          "Ставка фиксируется при открытии; выплата — в кошелёк у банка",
          "Досрочно возвращается всё тело + 50% от процентов; по сроку — полный процент",
          "Вклады не включают комиссию хранения сейфа сами по себе",
        ],
      },
      {
        type: "highlight",
        text: "Сейф и вклад — разные пулы. Нельзя открыть вклад из bankCoins и нельзя потратить вклад, пока он активен.",
      },
      {
        type: "note",
        text: "Долг за хранение оплачивается у банка (кнопка «Оплатить»). Пока долг есть, «положить» в сейф и открытие вклада недоступны.",
      },
    ],
  },
]

/** Правила настольной партии «Хранители» */
export const GUARDIANS_PHYSICAL_RULES_SECTIONS: GameRulesSection[] = [
  {
    number: 1,
    title: "Цель и что нужно",
    scope: "physical",
    blocks: [
      {
        type: "highlight",
        text: "Первым собрать 6 трофеев.",
      },
      {
        type: "bullets",
        bullets: [
          "Колода белых карт",
          "Набор карт для прокачки (зелёные → красные)",
          "Зоны на столе: актив (6), добор белых, резерв прокачки (6), запас прокачки, сброс, трофеи",
        ],
      },
    ],
  },
  {
    number: 2,
    title: "Подготовка",
    scope: "physical",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Колоду белых перетасовать",
          "Взять 6 белых наугад — это актив. Остальные белые — добор лицом вниз",
          "Из карт прокачки выбрать любые 6 — резерв прокачки. Остальные — запас прокачки",
          "Сброс и трофеи пустые",
          "В стартовом активе только белые. Цветная карта попадает в актив только через прокачку после победы",
        ],
      },
    ],
  },
  {
    number: 3,
    title: "Зоны",
    scope: "physical",
    blocks: [
      {
        type: "table",
        table: {
          headers: ["Зона", "Содержание", "Правило"],
          rows: [
            ["Актив (6)", "Карты, которыми ходишь", "Старт — белые; позже могут быть прокачанные"],
            ["Добор белых", "Остаток белых", "Пополняет актив"],
            ["Резерв прокачки (6)", "Кандидаты на прокачку", "В бой напрямую не выставляются"],
            ["Запас прокачки", "Остальные карты прокачки", "Доклад в резерв, когда слот свободен"],
            ["Сброс", "Проигравшие и снятые при прокачке", "При пустом доборе белых — перемешать в добор"],
            ["Трофеи", "Зачтённые победы", "До 6; в бою не участвуют"],
          ],
        },
      },
    ],
  },
  {
    number: 4,
    title: "Бой",
    scope: "physical",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Каждый тайно выбирает 1 карту из актива (карту со сном выбрать нельзя)",
          "Карты открываются одновременно",
          "Считается сила (см. ниже)",
          "У кого сила выше — победа. При ничьей карты возвращаются в активы владельцев",
        ],
      },
      {
        type: "paragraphs",
        paragraphs: [
          "Проигравший: его карта → сброс. Пустой слот актива → добрать белую из добора. Если добор пуст — перемешать сброс в добор и добрать. Если добрать нечего — играть оставшимися картами актива.",
        ],
      },
    ],
  },
  {
    number: 5,
    title: "Победитель — выбор",
    scope: "physical",
    intro: "Одно из двух.",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "А) Трофей: победившая карта → в трофеи; пустой слот актива → добрать белую",
          "Б) Прокачка (если можно): в резерве есть карта той же стихии и того же стиля, ранг ровно на 1 выше",
        ],
      },
      {
        type: "paragraphs",
        paragraphs: [
          "При прокачке: победившая карта → сброс. Карта из резерва встаёт в актив с меткой сон. В резерве освободился слот → можно доложить любую карту из запаса прокачки (не белые).",
          "Прокачка только по ступеням: Белая → Зелёная → Синяя → Фиолетовая → Оранжевая → Красная. Нет подходящей карты в резерве — прокачка недоступна, бери трофей.",
        ],
      },
    ],
  },
  {
    number: 6,
    title: "Сон и резерв",
    scope: "physical",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Сон — метка только у карты, только что пришедшей из прокачки",
          "Ею нельзя ходить в следующем бою; после этого боя сон снимается",
          "Уже «проснувшимися» сильными картами можно ходить каждый бой",
          "В резерве до 6 карт; слот свободен → доклад из запаса прокачки",
          "Белые в резерв прокачки класть нельзя",
          "Из резерва нельзя ходить в бой — только через выбор «прокачка» после победы",
        ],
      },
    ],
  },
  {
    number: 7,
    title: "Сила карты",
    scope: "physical",
    blocks: [
      {
        type: "paragraphs",
        paragraphs: [
          "Сравните итоговую силу обеих карт. База — номинал ранга (ориентир: белая 1, зелёная 2, синяя 3, фиолетовая 4, оранжевая 5, красная 6).",
        ],
      },
      {
        type: "bullets",
        bullets: [
          "Стихия побеждает стихию соперника → +100% от базы своей карты",
          "Стиль побеждает стиль соперника → +50% от базы своей карты",
          "Оба бонуса могут сложиться",
        ],
      },
      {
        type: "paragraphs",
        paragraphs: [
          "Стили: Атака бьёт Защиту; Защита бьёт Скорость; Скорость бьёт Атаку.",
          "Стихии — по кругу преимуществ игры «Хранители». Если преимущества нет — бонус стихии не даётся.",
        ],
      },
    ],
  },
  {
    number: 8,
    title: "Дальше и конец партии",
    scope: "physical",
    blocks: [
      {
        type: "bullets",
        bullets: [
          "Победа прокачанной картой → снова выбор: трофей или прокачка (+1 ранг, если карта есть в резерве)",
          "Поражение → карта в сброс; слот актива заполняется белой",
          "Трофей цветной считается как обычный трофей (1 из 6)",
          "Кто первым положил 6-й трофей — победил",
          "Трофеи до конца партии из зоны не возвращаются",
        ],
      },
      {
        type: "highlight",
        text: "Шпаргалка: 6 белых в активе + 6 в резерве → бой одной картой → проигрыш: сброс → добор белой → победа: трофей или прокачка (ранг +1, сон) → 6 трофеев — победа.",
      },
      {
        type: "note",
        text: "Белые — топливо боёв и трофеев; резерв — план прокачки; каждая победа — решение «зачесть трофей» или «усилить линию».",
      },
    ],
  },
]
